import React, { useState, useEffect, useRef, useMemo } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { IoIosSearch, IoIosArrowDown, IoMdClose } from "react-icons/io";
import { BsThreeDotsVertical } from "react-icons/bs";
import AppIcon from "../../components/Common/AppIcon";
import broadnetLogo from "../../assets/img/broadnet-logo.svg";
import cutomiseIcon from "../../assets/img/cutomiseIcon.svg";
import {
  toggleBulkSMSOverlay,
  toggleQuickSMSOverlay,
} from "../../components/Message/MessageSlice";
import { walletSelector } from "../../components/Wallet/WalletSlice";
import {
  isAbacRouteAllowed,
} from "../../components/User/Abac/abacRouteMap";
import { hasRoles, isSeller, isManager, checkForWalletMin } from "../../lib/utility";
import useAbacAccess from "../../hooks/useAbacAccess";

const isBalanceFieldPresent = (value) => value !== null && value !== undefined;
const formatBalanceAmount = (value) =>
  isBalanceFieldPresent(value) ? String(value) : "0";

const MobileNavDrawer = ({
  isOpen,
  onClose,
  user,
  profileData,
  abacPermissions,
  logout,
}) => {
  const { t } = useTranslation("sidebar");
  const { t: tNav } = useTranslation("navbar");
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const [searchQuery, setSearchQuery] = useState("");
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const moreMenuRef = useRef(null);
  const moreBtnRef = useRef(null);
  const drawerRef = useRef(null);

  const { balanceData: rawBalanceData } = useSelector(walletSelector);
  const canSeeDualBalance = hasRoles("tenant,superadmin,system", user);
  const personalCreditsAmount = formatBalanceAmount(rawBalanceData?.credits);
  const personalWalletAmount = formatBalanceAmount(
    rawBalanceData?.walletBalance ?? rawBalanceData?.wallets
  );
  const personalWalletCurrency = rawBalanceData?.currency || "";
  const walletFlagYes =
    rawBalanceData?.walletType?.toLowerCase() === "wallet" ||
    checkForWalletMin(rawBalanceData?.walletFlag);

  const isSellerOrManager = isSeller(user) || isManager(user);
  const { canAccessNavbar, canAccessWalletUi } = useAbacAccess();

  // Tracks open/closed state of section cards
  const [openCards, setOpenCards] = useState({
    customerEngagement: true,
    vendorProviders: false,
    organizationAccounts: false,
    messageOperations: false,
    analyticsReports: false,
    complianceSecurity: false,
    billingPricing: false,
    developers: false,
  });

  const toggleCard = (key) => {
    setOpenCards((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  // Auto-dismiss on route change
  useEffect(() => {
    if (isOpen) {
      onClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Escape key handler
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape" && isOpen) {
        onClose();
      }
    };
    if (isOpen) {
      window.addEventListener("keydown", handleKeyDown);
    }
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose]);

  // Body scroll locking
  useEffect(() => {
    if (isOpen) {
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = originalOverflow;
      };
    }
  }, [isOpen]);

  // Resize listener
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 768 && isOpen) {
        onClose();
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [isOpen, onClose]);

  // Close 3-dots menu on outside click
  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (
        showMoreMenu &&
        moreMenuRef.current &&
        !moreMenuRef.current.contains(e.target) &&
        !moreBtnRef.current?.contains(e.target)
      ) {
        setShowMoreMenu(false);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    document.addEventListener("touchstart", handleOutsideClick);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      document.removeEventListener("touchstart", handleOutsideClick);
    };
  }, [showMoreMenu]);

  // Check ABAC allowed route helper
  const isAllowed = (path, abacPath) => {
    if (!abacPath) return true;
    return isAbacRouteAllowed(path, abacPermissions, user, profileData, abacPath);
  };

  const canShowCustomizeNav =
    hasRoles("admin,tenant", user) && canAccessNavbar("customize_panel");
  const canShowNotificationsNav = canAccessNavbar("notifications");
  const canShowQuickMessageNav =
    !isSellerOrManager &&
    canAccessNavbar("quick_message") &&
    isAllowed("/message", ["sms", "message", "quick_send", "view"]);
  const canShowBulkMessageNav =
    !isSellerOrManager &&
    canAccessNavbar("bulk_message") &&
    isAllowed("/message", ["sms", "message", "bulk_campaign", "view"]);
  const canShowSubmissionReportNav =
    !isSellerOrManager && canAccessNavbar("submission_report");
  const canShowDownloadsNav = canAccessNavbar("downloads");
  const canShowWalletNav = !isSellerOrManager && canAccessWalletUi;

  const hasAnyMoreOptions =
    canShowCustomizeNav ||
    canShowNotificationsNav ||
    canShowQuickMessageNav ||
    canShowBulkMessageNav ||
    canShowDownloadsNav ||
    canShowSubmissionReportNav ||
    canShowWalletNav;

  const handleCustomizeFilterClick = () => {
    setShowMoreMenu(false);
    onClose();
    window.dispatchEvent(new CustomEvent("openCustomizePanelModal"));
  };

  const handleNotificationClick = () => {
    setShowMoreMenu(false);
    onClose();
    window.dispatchEvent(new CustomEvent("openNotificationModal"));
  };

  const handleQuickMessageClick = () => {
    setShowMoreMenu(false);
    onClose();
    navigate("/message?quickSms=true");
    dispatch(toggleQuickSMSOverlay(true));
  };

  const handleBulkMessageClick = () => {
    setShowMoreMenu(false);
    onClose();
    navigate("/message?bulkSms=true");
    dispatch(toggleBulkSMSOverlay(true));
  };

  const handleFileProgressClick = () => {
    setShowMoreMenu(false);
    onClose();
    window.dispatchEvent(new CustomEvent("openDownloadProgressModal"));
  };

  const handleSubmissionReportClick = () => {
    setShowMoreMenu(false);
    onClose();
    if (isSellerOrManager) {
      navigate("/reports/submission");
    } else {
      window.dispatchEvent(new CustomEvent("openSubmissionReportModal"));
    }
  };

  const handleWalletClick = () => {
    setShowMoreMenu(false);
    onClose();
    if (isSellerOrManager) {
      navigate("/reports/transaction-history");
    } else {
      navigate("/wallet");
    }
  };

  // Master definition of module groups and links matching design system
  const moduleSections = useMemo(() => {
    const isSellerUser = isSeller(user);
    const isManagerUser = isManager(user);

    if (isSellerUser) {
      return [
        {
          id: "sellerOperations",
          title: t("sections.messageOperations", { defaultValue: "Operations" }),
          items: [
            { name: t("seller.batch", { defaultValue: "Batch" }), to: "/reports/batch-summary", icon: "summary" },
            { name: t("seller.submission", { defaultValue: "Submission" }), to: "/reports/submission", icon: "submission" },
            { name: t("seller.performanceUser", { defaultValue: "Performance User" }), to: "/reports/performance-user", icon: "performance" },
          ],
        },
        {
          id: "sellerAccounts",
          title: t("sections.organizationAccounts", { defaultValue: "Accounts" }),
          items: [
            { name: t("seller.user", { defaultValue: "User" }), to: "/user", icon: "users" },
            { name: t("seller.transactionHistory", { defaultValue: "Transaction History" }), to: "/reports/transaction-history", icon: "transactionHistory" },
          ],
        },
      ];
    }

    if (isManagerUser) {
      return [
        {
          id: "managerOperations",
          title: t("sections.messageOperations", { defaultValue: "Operations" }),
          items: [
            { name: t("seller.submission", { defaultValue: "Submission" }), to: "/reports/submission", icon: "submission" },
            { name: t("seller.performanceUser", { defaultValue: "Performance User" }), to: "/reports/performance-user", icon: "performance" },
          ],
        },
      ];
    }

    // Default enterprise view
    return [
      {
        id: "customerEngagement",
        title: t("sections.customerEngagement", { defaultValue: "Customer Engagement" }),
        items: [
          { name: t("menu.messages", { defaultValue: "Messages" }), to: "/message", icon: "messages", abac: ["sms", "message"] },
          { name: t("menu.templates", { defaultValue: "Templates" }), to: "/template", icon: "templates", abac: ["sms", "template"] },
          { name: t("menu.twoWayMessaging", { defaultValue: "Two-Way Messaging" }), to: "/utilities/two-way", icon: "twoWayMessaging", abac: ["sms", "sms_utilities", "two_way"] },
          { name: t("menu.contacts", { defaultValue: "Contacts" }), to: "/address-book", icon: "contacts", abac: ["address_book"] },
          { name: t("menu.optInPages", { defaultValue: "Opt-in Pages" }), to: "/websmpp/subscription", icon: "optInPages", abac: ["sms", "subscription"] },
        ],
      },
      {
        id: "vendorProviders",
        title: t("sections.vendorsProviders", { defaultValue: "Provider & Partners" }),
        items: [
          { name: t("menu.vendors", { defaultValue: "Vendors" }), to: "/vendor", icon: "vendors", abac: ["vendor"] },
          { name: t("menu.providers", { defaultValue: "Providers" }), to: "/smsc/list", icon: "providers", abac: ["smsc"] },
          { name: t("menu.vendorSummary", { defaultValue: "Vendor Summary" }), to: "/reports/vendor-summary", icon: "vendorSummary", abac: ["sms", "reports", "vendor_summary"] },
          { name: t("menu.resellers", { defaultValue: "Resellers" }), to: "/sales", icon: "resellers", abac: ["sales_team"] },
        ],
      },
      {
        id: "organizationAccounts",
        title: t("sections.organizationAccounts", { defaultValue: "Organization & Accounts" }),
        items: [
          { name: t("menu.organizations", { defaultValue: "Organizations" }), to: "/company", icon: "organizations", abac: ["company"] },
          { name: t("menu.users", { defaultValue: "Users" }), to: "/user/list", icon: "users", abac: [["user", "profile"], ["user", "limit"]] },
          { name: t("menu.trafficLimits", { defaultValue: "Traffic Limits" }), to: "/user/limit", icon: "trafficLimits", abac: ["user", "limit"] },
        ],
      },
      {
        id: "messageOperations",
        title: t("sections.messageOperations", { defaultValue: "Message Operations" }),
        items: [
          { name: t("reports.summary", { defaultValue: "Summary" }), to: "/reports/summary/batch", icon: "summary", abac: ["sms", "reports", "summary"] },
          { name: t("reports.coverage", { defaultValue: "Coverage" }), to: "/reports/coverage-report", icon: "coverage", abac: ["sms", "reports", "coverage"] },
          { name: t("menu.pcap", { defaultValue: "Packet Capture (PCAP)" }), to: "/utilities/pcaps", icon: "packetCapture", abac: ["sms", "sms_utilities", "pcap"] },
          { name: t("menu.dltCompliance", { defaultValue: "DLT Compliance" }), to: "/dlt", icon: "dltCompliance", abac: [["dlt", "principle"], ["dlt", "template"]] },
        ],
      },
      {
        id: "analyticsReports",
        title: t("sections.analyticsReports", { defaultValue: "Analytics & Reports" }),
        items: [
          { name: t("reports.edr", { defaultValue: "EDR" }), to: "/reports/edr", icon: "edr", abac: ["sms", "reports", "edr"] },
          { name: t("reports.delivery", { defaultValue: "Delivery" }), to: "/reports/summary/delivery", icon: "delivery", abac: ["sms", "reports", "summary", "delivery"] },
          { name: t("reports.deliveryAdvance", { defaultValue: "Delivery Advance" }), to: "/reports/detailed/advanced", icon: "deliveryAdvance", abac: ["sms", "reports", "detailed_analytics", "delivery_advance"] },
          { name: t("reports.deliveryContent", { defaultValue: "Delivery Content" }), to: "/reports/detailed/content", icon: "deliveryContent", abac: ["sms", "reports", "detailed_analytics", "delivery_content"] },
          { name: t("reports.performance", { defaultValue: "Performance" }), to: "/reports/performance/user", icon: "performance", abac: ["sms", "reports", "performance"] },
          { name: t("reports.lookup", { defaultValue: "Look-up" }), to: "/reports/lookup", icon: "lookup", abac: ["sms", "reports", "lookup"] },
          { name: t("menu.alerts", { defaultValue: "Alerts" }), to: "/smsc/alert", icon: "alerts", abac: ["smsc", "alert"] },
          { name: t("menu.reports", { defaultValue: "Submissions" }), to: "/reports/submission", action: "submissionModal", icon: "submission", abac: ["sms", "reports"] },
        ],
      },
      {
        id: "complianceSecurity",
        title: t("sections.complianceSecurity", { defaultValue: "Compliance & Security" }),
        items: [
          { name: t("menu.firewall", { defaultValue: "Firewall" }), to: "/firewall", icon: "firewall", abac: ["sms", "firewall"] },
          { name: t("menu.networks", { defaultValue: "Networks" }), to: "/network", icon: "networks", abac: ["network"] },
          { name: t("reports.blocked", { defaultValue: "Blocked" }), to: "/reports/blocked", icon: "blocked", abac: ["sms", "reports", "blocked"] },
          { name: t("menu.trafficBlocking", { defaultValue: "Traffic Blocking" }), to: "/smsc/blocking", icon: "trafficBlocking", abac: ["smsc", "blocking"] },
        ],
      },
      {
        id: "billingPricing",
        title: t("sections.billingRouting", { defaultValue: "Billing & Routing" }),
        items: [
          { name: t("menu.pricing", { defaultValue: "Rates" }), to: "/routing", icon: "crdEur", abac: ["sms", "rates"] },
          { name: t("menu.routing", { defaultValue: "Route" }), to: "/routing", icon: "route", abac: ["sms", "routing"] },
          { name: t("menu.senderBasedRouting", { defaultValue: "Sender-Based Routing" }), to: "/routing/sender/add", icon: "senderBasedRouting", abac: ["sms", "sender_routing"] },
          { name: t("menu.transactionHistory", { defaultValue: "Transaction History" }), to: "/wallet", icon: "transactionHistory", abac: ["sms", "transactions"] },
        ],
      },
      {
        id: "developers",
        title: t("sections.developers", { defaultValue: "Developers" }),
        items: [
          { name: t("menu.errorCodes", { defaultValue: "Error Codes" }), to: "/user/error-code", icon: "errorCodes", abac: ["developers", "error_codes"] },
          { name: t("menu.encodingTools", { defaultValue: "Encoding Tools" }), to: "/utilities/converter", icon: "encodingTools", abac: ["sms", "sms_utilities", "character_converter"] },
          { name: t("menu.sdks", { defaultValue: "SDKs" }), to: "/developer/sdk", icon: "sdk", abac: ["platform_documents", "cpaas_business_api"] },
          { name: t("menu.documents", { defaultValue: "Documentation" }), to: "/downloads", icon: "documentation", abac: ["platform_documents", "cpaas_documentation", "features_documents"] },
        ],
      },
    ];
  }, [user, t]);

  // Filter sections by ABAC and search query
  const filteredSections = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();

    return moduleSections
      .map((section) => {
        const allowedItems = section.items.filter((item) =>
          isAllowed(item.to, item.abac)
        );

        if (!q) {
          return {
            ...section,
            items: allowedItems,
          };
        }

        const matchingItems = allowedItems.filter((item) =>
          item.name.toLowerCase().includes(q)
        );

        return {
          ...section,
          items: matchingItems,
        };
      })
      .filter((section) => section.items.length > 0);
  }, [moduleSections, searchQuery, abacPermissions, user, profileData]);

  // Total matching count for no results check
  const totalMatches = filteredSections.reduce(
    (acc, sec) => acc + sec.items.length,
    0
  );

  const userName = profileData?.userName || user?.name || "super";
  const userRole = profileData?.roles || profileData?.role || user?.role || "Superadmin";
  const avatarSrc =
    profileData?.profilePath && profileData?.profileName
      ? `data:image/${profileData.profileName};base64,${profileData.profilePath}`
      : broadnetLogo;

  const showDashboard =
    !searchQuery.trim() ||
    t("menu.dashboard", { defaultValue: "Dashboard" })
      .toLowerCase()
      .includes(searchQuery.trim().toLowerCase());

  if (!isOpen && typeof document === "undefined") return null;

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <div className="mobile-drawer-portal-root">
          {/* Backdrop Overlay */}
          <motion.div
            className="mobile-drawer-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
            onClick={onClose}
            aria-hidden="true"
          />

          {/* External Floating Dismiss Button */}
          <motion.button
            type="button"
            className="mobile-drawer-dismiss-btn"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
            onClick={onClose}
            aria-label="Close navigation drawer"
          >
            <IoMdClose size={18} />
          </motion.button>

          {/* Sliding Drawer Container */}
          <motion.aside
            ref={drawerRef}
            className="mobile-nav-drawer"
            initial={{ x: "-100%" }}
            animate={{ x: 0 }}
            exit={{ x: "-100%" }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            role="dialog"
            aria-modal="true"
            aria-label="Mobile Navigation"
          >
            {/* A. Profile Header & Quick Options Menu */}
            <div className="mobile-drawer-profile">
              <div
                className="mobile-drawer-user-row"
                onClick={() => {
                  onClose();
                  navigate("/profile");
                }}
                role="button"
                tabIndex={0}
              >
                <div className="mobile-drawer-avatar-badge">
                  <img
                    src={avatarSrc}
                    alt={userName}
                    className="prevent-dark-icon"
                    loading="lazy"
                  />
                </div>
                <div className="mobile-drawer-user-info">
                  <span className="mobile-drawer-user-name" title={userName}>
                    {userName}
                  </span>
                  <span className="mobile-drawer-user-role">{userRole}</span>
                </div>
              </div>

              {/* 3-dots trigger button */}
              {hasAnyMoreOptions && (
                <button
                  ref={moreBtnRef}
                  type="button"
                  className="mobile-drawer-more-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowMoreMenu((prev) => !prev);
                  }}
                  aria-label="Quick options menu"
                  aria-expanded={showMoreMenu}
                >
                  <BsThreeDotsVertical size={16} />
                </button>
              )}

              {/* Floating 3-dots Quick Options Menu */}
              {showMoreMenu && hasAnyMoreOptions && (
                <div ref={moreMenuRef} className="mobile-drawer-more-menu">
                  {canShowCustomizeNav && (
                    <button
                      type="button"
                      className="mobile-drawer-more-item"
                      onClick={handleCustomizeFilterClick}
                    >
                      <img src={cutomiseIcon} alt="" style={{ width: 16, height: 16 }} />
                      <span>{tNav("menu.customizePanel", { defaultValue: tNav("tooltips.customizePanel", { defaultValue: "Customize Panel" }) })}</span>
                    </button>
                  )}

                  {canShowNotificationsNav && (
                    <button
                      type="button"
                      className="mobile-drawer-more-item"
                      onClick={handleNotificationClick}
                    >
                      <AppIcon name="notification" size={16} />
                      <span>{tNav("tooltips.notifications", { defaultValue: t("tooltips.notifications", { defaultValue: "Notifications" }) })}</span>
                    </button>
                  )}

                  {canShowQuickMessageNav && (
                    <button
                      type="button"
                      className="mobile-drawer-more-item"
                      onClick={handleQuickMessageClick}
                    >
                      <AppIcon name="quickMessage" size={16} />
                      <span>{tNav("tooltips.quickMessage", { defaultValue: t("tooltips.quickMessage", { defaultValue: "Quick Message" }) })}</span>
                    </button>
                  )}

                  {canShowBulkMessageNav && (
                    <button
                      type="button"
                      className="mobile-drawer-more-item"
                      onClick={handleBulkMessageClick}
                    >
                      <AppIcon name="bulkMessage" size={16} />
                      <span>{tNav("tooltips.bulkMessage", { defaultValue: t("tooltips.bulkMessage", { defaultValue: "Bulk Message" }) })}</span>
                    </button>
                  )}

                  {canShowDownloadsNav && (
                    <button
                      type="button"
                      className="mobile-drawer-more-item"
                      onClick={handleFileProgressClick}
                    >
                      <AppIcon name="fileDownloadProgress" size={16} />
                      <span>{tNav("tooltips.fileDownloadProgress", { defaultValue: t("tooltips.fileDownloadProgress", { defaultValue: "File download progress" }) })}</span>
                    </button>
                  )}

                  {canShowSubmissionReportNav && (
                    <button
                      type="button"
                      className="mobile-drawer-more-item"
                      onClick={handleSubmissionReportClick}
                    >
                      <AppIcon name="submissionReport" size={16} />
                      <span>{tNav("tooltips.submissionReport", { defaultValue: t("tooltips.submissionReport", { defaultValue: "Submission Report" }) })}</span>
                    </button>
                  )}

                  {canShowWalletNav && (
                    canSeeDualBalance ? (
                      <>
                        <button
                          type="button"
                          className="mobile-drawer-more-item"
                          onClick={handleWalletClick}
                        >
                          <AppIcon name="crdEur" size={16} />
                          <span>{personalCreditsAmount} CRD</span>
                        </button>

                        <button
                          type="button"
                          className="mobile-drawer-more-item"
                          onClick={handleWalletClick}
                        >
                          <AppIcon name="wallet" size={16} />
                          <span>
                            {personalWalletAmount} {personalWalletCurrency || "EUR"}
                          </span>
                        </button>
                      </>
                    ) : (
                      <button
                        type="button"
                        className="mobile-drawer-more-item"
                        onClick={handleWalletClick}
                      >
                        <AppIcon name="crdEur" size={16} />
                        <span>
                          {walletFlagYes
                            ? `${personalWalletAmount} ${personalWalletCurrency || "EUR"}`.trim()
                            : `${personalCreditsAmount} CRD`}
                        </span>
                      </button>
                    )
                  )}
                </div>
              )}
            </div>

            {/* B. Quick Search Box & Top Module Card */}
            <div className="mobile-drawer-top-card">
              <div className="mobile-drawer-search-wrap">
                <IoIosSearch className="mobile-drawer-search-icon" aria-hidden />
                <input
                  type="search"
                  className="mobile-drawer-search-input"
                  placeholder={t("tooltips.search", { defaultValue: "Search" })}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  aria-label="Search navigation"
                />
                {searchQuery.trim() && (
                  <button
                    type="button"
                    className="mobile-drawer-search-clear"
                    onClick={() => setSearchQuery("")}
                    aria-label="Clear search"
                  >
                    ×
                  </button>
                )}
              </div>

              {/* Primary Top Module: Dashboard */}
              {showDashboard && isAllowed("/dashboard", ["sms", "dashboard"]) && (
                <NavLink
                  to="/dashboard"
                  className={({ isActive }) =>
                    `mobile-drawer-dashboard-link ${isActive ? "active" : ""}`
                  }
                  onClick={onClose}
                >
                  <div className="d-flex align-items-center gap-2">
                    <span className="mobile-drawer-dashboard-icon">
                      <AppIcon name="dashboard" size={18} />
                    </span>
                    <span>{t("menu.dashboard", { defaultValue: "Dashboard" })}</span>
                  </div>
                  <IoIosArrowDown className="mobile-drawer-dashboard-chevron" />
                </NavLink>
              )}
            </div>

            {/* C. Card-Style Module Group Sections */}
            <div className="mobile-drawer-sections-flow">
              {filteredSections.map((section) => {
                const isOpenCard = searchQuery.trim()
                  ? true
                  : !!openCards[section.id];

                return (
                  <div key={section.id} className="mobile-module-card">
                    <button
                      type="button"
                      className={`mobile-module-card-header ${isOpenCard ? "open" : ""}`}
                      onClick={() => toggleCard(section.id)}
                      aria-expanded={isOpenCard}
                    >
                      <span className="mobile-module-card-title">
                        {section.title}
                      </span>
                      <span
                        className={`mobile-module-card-chevron ${isOpenCard ? "open" : ""}`}
                      >
                        <IoIosArrowDown />
                      </span>
                    </button>

                    <AnimatePresence initial={false}>
                      {isOpenCard && (
                        <motion.div
                          className="mobile-module-card-body"
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.22, ease: "easeOut" }}
                        >
                          {section.items.map((item) =>
                            item.action === "submissionModal" && !isSellerOrManager ? (
                              <button
                                key={item.name}
                                type="button"
                                className="mobile-module-link mobile-module-btn"
                                onClick={handleSubmissionReportClick}
                              >
                                <span className="mobile-module-link-icon">
                                  <AppIcon name={item.icon} size={18} />
                                </span>
                                <span className="mobile-module-link-label">
                                  {item.name}
                                </span>
                              </button>
                            ) : (
                              <NavLink
                                key={item.to}
                                to={item.to}
                                className={({ isActive }) =>
                                  `mobile-module-link ${isActive ? "active" : ""}`
                                }
                                onClick={onClose}
                              >
                                <span className="mobile-module-link-icon">
                                  <AppIcon name={item.icon} size={18} />
                                </span>
                                <span className="mobile-module-link-label">
                                  {item.name}
                                </span>
                              </NavLink>
                            )
                          )}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                );
              })}

              {/* No matching results fallback */}
              {searchQuery.trim() && totalMatches === 0 && !showDashboard && (
                <div className="mobile-search-no-results">
                  <div className="mobile-search-no-results-divider" />
                  <p>No matching results</p>
                </div>
              )}
            </div>

            {/* D. Bottom Action / Logout Button */}
            <button
              type="button"
              className="mobile-drawer-logout"
              onClick={() => {
                onClose();
                logout();
              }}
              aria-label={t("menu.logout", { defaultValue: "Logout" })}
            >
              <AppIcon name="logout" size={18} />
              <span>{t("menu.logout", { defaultValue: "Logout" })}</span>
            </button>
          </motion.aside>
        </div>
      )}
    </AnimatePresence>,
    document.body
  );
};

export default MobileNavDrawer;

